import Sortable from "sortablejs";

/**
 * The canonical SortableJS `onEnd` fixup for an Alpine keyed-`x-for` list.
 *
 * SortableJS physically moves the dragged node in the DOM before `onEnd` fires.
 * If we only mutate the backing array, Alpine reconciles its keyed x-for against
 * a DOM Sortable already changed and "moves" the node a SECOND time → the visible
 * snap-back + desynced order badges. So we FIRST undo Sortable's DOM mutation
 * (put `item` back at its original slot), THEN reorder the data array, letting
 * Alpine re-render the move purely from the (returned) array.
 *
 * Index over the `[${rowSelector}]` rows only (NOT el.children): the x-for
 * <template> anchor is el.children[0] and any empty-state node carries no row
 * attribute, so el.children indices are off-by-one. After Sortable's move `item`
 * sits at newIndex among the rows; to restore it to oldIndex we insert before the
 * row currently at the original slot (one further along when the item moved
 * leftward, since `item` now precedes it).
 *
 * Returns a NEW array with the element moved oldIndex → newIndex (caller assigns
 * it back so Alpine sees a fresh reference). Returns the input unchanged when the
 * event is a no-op (missing/equal indices).
 */
export function applySortableReorder<T>(
  el: HTMLElement,
  evt: Sortable.SortableEvent,
  data: T[],
  rowSelector: string,
): T[] {
  const { oldIndex, newIndex, item } = evt;
  if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return data;
  const rows = el.querySelectorAll(`[${rowSelector}]`);
  const ref = rows[oldIndex > newIndex ? oldIndex + 1 : oldIndex] ?? null;
  el.insertBefore(item, ref);
  const next = [...data];
  const [moved] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, moved);
  return next;
}

/** Options for {@link createReorderable} — the per-call-site bits. The reduce-motion
 *  `animation` and the `ghostClass` are baked in by the helper. */
export interface ReorderableOptions {
  /** Drag-handle selector. Omit for whole-element drag (e.g. the amenity chip grid). */
  handle?: string;
  /** Which children are draggable. */
  draggable?: string;
  /** SortableJS group, for cross-list drag (e.g. the amenity chip grids share one). */
  group?: Sortable.Options["group"];
  onEnd: (evt: Sortable.SortableEvent) => void;
}

/**
 * `Sortable.create` with the CMS editor's universal defaults baked in: the
 * `animation` honors `prefers-reduced-motion` (0 when the user opts out, else
 * 150ms), `ghostClass` is the standard `'opacity-30'`, and the drag-autoscroll
 * is tuned (see below). Every reduce-motion-aware reorderable in the editor
 * re-typed that same boilerplate; centralizing it here keeps the defaults from
 * drifting. Every editor reorderable — the transactional pickers, curation-modal,
 * and image-picker — is routed through this so none can drift again.
 *
 * Autoscroll: SortableJS's defaults (native HTML5-drag autoscroll,
 * `scrollSensitivity: 30`) give a narrow scroll band AND a dead zone at the
 * extreme top/bottom of the scroll container — on Chrome/Firefox the browser
 * drives autoscroll off `dragover`, which isn't delivered at the viewport
 * edges. `forceAutoScrollFallback: true` forces SortableJS's own JS autoscroll
 * (driven by pointer position via `elementFromPoint`), which has no edge dead
 * zone; the wider `scrollSensitivity` fixes the narrow-band feel. Applies to
 * EVERY editor reorderable routed through here (they all shared the same poor
 * default), most visibly the long POI board list.
 */
export function createReorderable(el: HTMLElement, opts: ReorderableOptions): Sortable {
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  return Sortable.create(el, {
    animation: reduce ? 0 : 150,
    ghostClass: "opacity-30",
    handle: opts.handle,
    draggable: opts.draggable,
    group: opts.group,
    onEnd: opts.onEnd,
    scroll: true,
    forceAutoScrollFallback: true,
    scrollSensitivity: 80,
    scrollSpeed: 12,
  });
}

/** A grid that backs a Sortable list of id-chips. `key` addresses the backing
 *  array ('' == the derived unassigned grid, which has NO backing array);
 *  `ids` is the live array to mutate (undefined for unassigned). */
export interface ChipGrid {
  key: string;
  ids: string[] | undefined;
}

/**
 * The cross-grid SortableJS `onEnd` fixup for moving a chip between two
 * `group`-shared keyed-`x-for` grids (the amenity-grouping shelves + the derived
 * unassigned grid).
 *
 * Same revert-then-mutate principle as applySortableReorder, but across two
 * lists: SortableJS physically moved the chip from `evt.from` into `evt.to`; if
 * we only mutate the arrays, Alpine reconciles BOTH keyed x-fors against a DOM
 * already changed and double-moves. So we FIRST undo Sortable's DOM move (put the
 * chip back where it came from in `evt.from`), THEN mutate both arrays, letting
 * Alpine re-render each grid purely from its array.
 *
 * Resolution: `evt.from`/`evt.to` carry `data-tab-key`; we look up the matching
 * ChipGrid by key. The dragged id is `data-amenity-id` on the chip. The unassigned
 * grid (key '') has no backing array → as a SOURCE we simply don't splice (the id
 * was derived, not stored); as a TARGET we don't insert (it'll re-derive once the
 * id is removed from its source — i.e. equivalent to moveChip(id, null)).
 *
 * No-op (no DOM revert, no mutation) when from === to — that's a same-grid reorder
 * the caller should route to applySortableReorder instead.
 */
export function applySortableMove(evt: Sortable.SortableEvent, grids: ChipGrid[]): void {
  const { from, to, item, oldIndex, newIndex } = evt;
  if (from === to) return;

  // 1. Revert Sortable's DOM move: put the chip back at its original slot in
  //    `from`. Index over the [data-chip] ROWS (NOT from.children): the Alpine
  //    x-for <template> anchor is from.children[0], so children indices are
  //    off-by-one. The item was moved OUT into `to`, so `from`'s rows no longer
  //    include it — inserting before the row now at oldIndex restores its slot.
  const fromRows = from.querySelectorAll("[data-chip]");
  from.insertBefore(item, fromRows[oldIndex ?? 0] ?? null);

  const id = item.getAttribute("data-amenity-id");
  if (!id) return;
  const fromKey = from.getAttribute("data-tab-key") ?? "";
  const toKey = to.getAttribute("data-tab-key") ?? "";
  const byKey = (k: string) => grids.find((g) => g.key === k);

  // 2a. Remove from the SOURCE array ('' source == unassigned → derived, no splice).
  const src = byKey(fromKey)?.ids;
  if (src) {
    const i = src.indexOf(id);
    if (i !== -1) src.splice(i, 1);
  }

  // 2b. Insert into the TARGET array at newIndex ('' target == unassigned → derived,
  //     no insert; once removed from source it re-derives into the unassigned grid).
  const dst = byKey(toKey)?.ids;
  if (dst && !dst.includes(id)) {
    const at = newIndex === undefined ? dst.length : Math.min(newIndex, dst.length);
    dst.splice(at, 0, id);
  }
}
